import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import {
  debounceTime,
  EMPTY,
  exhaustMap,
  filter,
  map,
  merge,
  of,
  switchMap,
  tap,
} from 'rxjs';

import { Option } from '@sapphire/result';
import { match } from 'ts-pattern';

import { Nullable } from '@ps/utils';
import { EffectsBase } from '@ps/core-data-access';
import { AuthSelectorsFacade } from '@ps/auth-data-access';
import { APP_ROUTES } from '@ps/core-config';
import { ToastService } from '@ps/core-overlay';
import { SURVEY_ACTIVITY_LOG_MESSAGES } from '@ps/survey-utils';
import {
  UserSettingsActionsFacade,
  UserSettingsSelectorsFacade,
} from '@ps/user-settings-data-access';
import {
  SurveyActivityLogActions,
  SurveyService,
} from '@ps/survey-data-access';

import { SurveyForm } from '../../utils';
import { SurveyScoreService } from '../../services/survey-score/survey-score.service';
import { FeasibilityService } from '../../services/feasibility/feasibility.service';
import { SurveyProcessingService } from '../../services/survey-processing/survey-processing.service';
import {
  SurveyCreationActions,
  SurveyCreationApiActions,
  SurveyCreationNavbarActions,
  SurveyCreationNavbarApiActions,
} from './survey-creation-audience.actions';
import {
  selectMetadataForm,
  selectSurvey,
  selectSurveyFormState,
  selectSurveyId,
} from './survey-creation-audience.selectors';
import { RecalculationTrigger } from './types/recalculation-action-trigger.types';

@Injectable()
export class SurveyCreationAudienceEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);

  private readonly _toastService = inject(ToastService);
  private readonly _router = inject(Router);

  private readonly _surveyService = inject(SurveyService);
  private readonly _surveyScoreService = inject(SurveyScoreService);
  private readonly _surveyProcessingService = inject(SurveyProcessingService);
  private readonly _feasibilityService = inject(FeasibilityService);

  private readonly _authSelectors = inject(AuthSelectorsFacade);
  private readonly _userSettingsSelectors = inject(UserSettingsSelectorsFacade);
  private readonly _userSettingsActions = inject(UserSettingsActionsFacade);

  public readonly createStandardSurvey$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationActions.createStandardSurvey),
      concatLatestFrom(() => [
        this._store.select(selectSurveyId),
        this._store.select(selectSurveyFormState),
      ]),
      map(([, surveyId, formState]) => ({
        surveyId,
        form: SurveyForm.from(formState),
      })),
      tap(({ form }) => {
        if (form.showErrors(true)) {
          this._toastError(form.error);
        }
      }),
      filter(
        ({ surveyId, form }) =>
          !surveyId && form.valid && form.isStandardSurvey(),
      ),
      map(({ form }) => ({
        user: this._userData(),
        ...Option.from(form.data).unwrap(),
      })),
      exhaustMap(({ user, metadata }) =>
        this._surveyService
          .createStandardSurvey(metadata, user.id, user.companyId)
          .pipe(
            map((survey) => {
              this._toastService.success('Survey successfully saved', {
                keepOpen: false,
              });
              return SurveyCreationApiActions.createStandardSurveySuccess({
                survey,
              });
            }),
            this.psCatchError(
              (error) =>
                of(
                  SurveyCreationApiActions.createStandardSurveyFailure({
                    error: error.message,
                  }),
                ),
              { notify: true },
            ),
          ),
      ),
    ),
  );

  // The create page hands the survey off to the edit route as soon as it exists.
  // Safe now that the edit page renders the Advanced Targeting / Set Location
  // block and hydrates a saved survey's qualifications — the reason this was
  // held back while the creation route stood alone.
  public readonly createStandardSurveyNavigate$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(SurveyCreationApiActions.createStandardSurveySuccess),
        tap(({ survey }) => {
          this._router.navigate([APP_ROUTES.editSurvey(survey.id)]);
        }),
      ),
    { dispatch: false },
  );

  public readonly saveStandardSurvey$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationActions.saveSurvey),
      concatLatestFrom(() => [
        this._store.select(selectSurveyId),
        this._store.select(selectSurveyFormState),
      ]),
      map(([, surveyId, formState]) => ({
        surveyId,
        form: SurveyForm.from(formState),
      })),
      tap(({ form }) => {
        if (form.showErrors()) {
          this._toastError(form.error);
        }
      }),
      filter(
        ({ surveyId, form }) =>
          !!surveyId && form.valid && form.isStandardSurvey(),
      ),
      map(({ form, surveyId }) => ({
        user: this._userData(),
        surveyId: Option.from(surveyId).unwrap(),
        ...Option.from(form.data).unwrap(),
      })),
      switchMap(({ user, surveyId, metadata }) =>
        this._surveyService
          .updateStandardSurvey(surveyId, metadata, user.id, user.companyId)
          .pipe(
            map((survey) => {
              this._toastService.success('Survey successfully saved', {
                keepOpen: false,
              });
              return SurveyCreationApiActions.saveSurveySuccess({
                survey,
              });
            }),
            this.psCatchError(
              (error) =>
                of(
                  SurveyCreationApiActions.saveSurveyFailure({
                    error: error.message,
                  }),
                ),
              { notify: true },
            ),
          ),
      ),
    ),
  );

  public readonly loadSurveyForEdit$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationActions.init),
      filter(
        (action): action is typeof action & { surveyId: number } =>
          !!action.surveyId,
      ),
      concatLatestFrom(() => this._store.select(selectSurvey)),
      // Skip the fetch when we already hold this exact survey in memory —
      // e.g. right after create → edit, where the create response already
      // has everything loadSurveyForEdit would otherwise re-fetch.
      filter(([{ surveyId }, survey]) => !survey || survey.id !== surveyId),
      switchMap(([{ surveyId }]) =>
        this._surveyService.getByIdTargeting(surveyId).pipe(
          map((survey) =>
            SurveyCreationApiActions.loadSurveyForEditSuccess({ survey }),
          ),
          this.psCatchError((error) =>
            of(
              SurveyCreationApiActions.loadSurveyForEditFailure({
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public readonly toggleManualProcessing$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationNavbarActions.toggleManualProcessing),
      filter(() =>
        this._userSettingsSelectors.buyerConfigSettingsId().isSome(),
      ),
      switchMap(({ companyId, enabled }) => {
        const buyerConfigSettingsId = this._userSettingsSelectors
          .buyerConfigSettingsId()
          .unwrap();

        return this._surveyProcessingService
          .updateManualProcessing(buyerConfigSettingsId, companyId, enabled)
          .pipe(
            tap(() => {
              this._toastService.success(
                'Processing setting updated successfully',
              );
            }),
            tap(() => {
              this._userSettingsActions.updateBuyerSetting(
                'enableManualProcessing',
                companyId,
                enabled,
              );
            }),
            switchMap(() => {
              const logAction = SurveyActivityLogActions.logOperatorActivity({
                log: {
                  message: SURVEY_ACTIVITY_LOG_MESSAGES.manualProcessingToggled,
                  metadata: { action: enabled ? 'ON' : 'OFF' },
                },
              });

              return of(logAction);
            }),
            this.psCatchError(() => EMPTY, { notify: true }),
          );
      }),
    ),
  );

  public readonly computeScore$ = createEffect(() => {
    const surveyChangeTrigger$ = this._actions$.pipe(
      ofType(
        SurveyCreationActions.updateMetadata,
        SurveyCreationNavbarActions.toggleManualProcessing,
        SurveyCreationApiActions.loadSurveyForEditSuccess,
        SurveyCreationApiActions.saveSurveySuccess,
      ),
      concatLatestFrom(() => this._store.select(selectSurveyId)),
      filter(([, surveyId]) => !!surveyId),
    );

    return merge(
      surveyChangeTrigger$.pipe(
        filter(([action]) => this._shouldMarkAsPending(action)),
        map(() => SurveyCreationNavbarApiActions.computeScorePending()),
      ),
      surveyChangeTrigger$.pipe(
        filter(([action]) => this._qualifiesForRecalculation(action)),
        debounceTime(500),
        map(([, surveyId]) => Option.from(surveyId).unwrap()),
        concatLatestFrom(() => this._store.select(selectMetadataForm)),
        filter(([surveyId, metadata]) => !!surveyId && !!metadata?.valid),
        map(([surveyId, metadata]) => ({
          surveyId,
          value: Option.from(metadata.value).map((value) => ({
            loi: Option.from(value.loi).unwrapOr(0),
            incidence: Option.from(value.incidence).unwrapOr(0),
          })),
        })),
        filter(({ value }) => value.isSome()),
        map(({ surveyId, value }) => ({ surveyId, value: value.unwrap() })),
        switchMap(({ surveyId, value }) =>
          this._surveyScoreService
            .computeScore(surveyId, {
              // TODO: Update CPI when we have Flexibility Prices
              cpi: 0, // Price Override OR ACPI OR 0
              ir: value.incidence,
              loi: value.loi,
            })
            .pipe(
              map(({ score, scorePercentage }) =>
                SurveyCreationNavbarApiActions.computeScoreSuccess({
                  score,
                  scorePercentage,
                }),
              ),
              this.psCatchError((error) =>
                of(
                  SurveyCreationNavbarApiActions.computeScoreFailure({
                    error: error.message,
                  }),
                ),
              ),
            ),
        ),
      ),
    );
  });

  public readonly recalculateFeasibility$ = createEffect(() => {
    const surveyChangeTrigger$ = this._actions$.pipe(
      ofType(
        SurveyCreationApiActions.loadSurveyForEditSuccess,
        SurveyCreationApiActions.saveSurveySuccess,
        SurveyCreationActions.updateMetadata,
        SurveyCreationActions.updateQualifications,
        SurveyCreationActions.updateQuotas,
        SurveyCreationNavbarActions.toggleManualProcessing,
        SurveyCreationNavbarActions.recalculateFeasibility,
      ),
      concatLatestFrom(() => this._store.select(selectSurveyId)),
      filter(([, surveyId]) => !!surveyId),
    );

    return merge(
      surveyChangeTrigger$.pipe(
        filter(([action]) => this._shouldMarkAsPending(action)),
        map(() =>
          SurveyCreationNavbarApiActions.recalculateFeasibilityPending(),
        ),
      ),
      surveyChangeTrigger$.pipe(
        filter(([action]) => this._qualifiesForRecalculation(action)),
        debounceTime(500),
        map(([, surveyId]) => Option.from(surveyId).unwrap()),
        concatLatestFrom(() => this._store.select(selectSurveyFormState)),
        map(([surveyId, formState]) => ({
          surveyId,
          form: SurveyForm.from(formState),
        })),
        filter(({ form }) => form.valid),
        map(({ surveyId, form }) => ({
          user: this._userData(),
          surveyId,
          ...Option.from(form.data).unwrap(),
        })),
        switchMap((data) =>
          this._feasibilityService.compute({ ...data }).pipe(
            map(({ total, quotaGroups, audience, hasQuota }) =>
              SurveyCreationNavbarApiActions.recalculateFeasibilitySuccess({
                total,
                quotaGroups,
                audience,
                hasQuota,
              }),
            ),
            this.psCatchError((error) =>
              of(
                SurveyCreationNavbarApiActions.recalculateFeasibilityFailure({
                  error: error.message,
                }),
              ),
            ),
          ),
        ),
      ),
    );
  });

  private _userData(): { id: number; email: string; companyId: number } {
    const user = this._authSelectors.user().unwrap();
    return {
      id: user.id,
      email: user.email,
      companyId: this._authSelectors.activeCompanyId(),
    };
  }

  private _toastError(error: Nullable<string>): void {
    Option.from(error).inspect((message) => this._toastService.error(message));
  }

  // True whenever Score/Feasibility should show as pending: either it is
  // actually about to recalculate, or it can't right now because manual
  // processing is blocking it — and will stay pending until Save or
  // toggling manual processing back off clears it.
  private _shouldMarkAsPending(action: RecalculationTrigger): boolean {
    return match(action)
      .with(
        { type: SurveyCreationNavbarActions.toggleManualProcessing.type },
        (toggled) => !toggled.enabled,
      )
      .otherwise(() => true);
  }

  // Defines which actions should trigger a recalculation of Score and Feasibility.
  private _qualifiesForRecalculation(action: RecalculationTrigger): boolean {
    return match(action)
      .with(
        { type: SurveyCreationNavbarActions.toggleManualProcessing.type },
        (toggled) => !toggled.enabled,
      )
      .with(
        { type: SurveyCreationApiActions.loadSurveyForEditSuccess.type },
        { type: SurveyCreationApiActions.saveSurveySuccess.type },
        { type: SurveyCreationNavbarActions.recalculateFeasibility.type },
        () => true,
      )
      .otherwise(
        () =>
          !this._userSettingsSelectors.buyerSettings().enableManualProcessing,
      );
  }
}
